import requests from "../requests";
import { Dispatch } from "redux";

export enum PatternsConstants {
  PATTERNS_REQUEST = "PATTERNS_REQUEST",
  PATTERNS_SUCCESS = "PATTERNS_SUCCESS",
  PATTERNS_FAILURE = "PATTERNS_FAILURE",

  NEW_PATTERNS_REQUEST = "NEW_PATTERNS_REQUEST",
  NEW_PATTERNS_SUCCESS = "NEW_PATTERNS_SUCCESS",
  NEW_PATTERNS_FAILURE = "NEW_PATTERNS_FAILURE",

  PATTERNS_SUMMARY_REQUEST = "PATTERNS_SUMMARY_REQUEST",
  PATTERNS_SUMMARY_SUCCESS = "PATTERNS_SUMMARY_SUCCESS",
  PATTERNS_SUMMARY_FAILURE = "PATTERNS_SUMMARY_FAILURE",

  PATTERNS_FEATURED_REQUEST = "PATTERNS_FEATURED_REQUEST",
  PATTERNS_FEATURED_SUCCESS = "PATTERNS_FEATURED_SUCCESS",
  PATTERNS_FEATURED_FAILURE = "PATTERNS_FEATURED_FAILURE",
}

const patternsService = {
  async getPatterns(filters?: {
    date?: string
    pattern_type?: string
    ticker?: string
    min_confidence?: number
    new_only?: boolean
    lookback_days?: number
    limit?: number
  }): Promise<PatternsResponse> {
    const params = new URLSearchParams();
    if (filters?.date) params.append('date', filters.date);
    if (filters?.pattern_type) params.append('pattern_type', filters.pattern_type);
    if (filters?.ticker) params.append('ticker', filters.ticker);
    if (filters?.min_confidence !== undefined) params.append('min_confidence', filters.min_confidence.toString());
    if (filters?.new_only) params.append('new_only', 'true');
    if (filters?.lookback_days) params.append('lookback_days', filters.lookback_days.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const resp = await requests.get(`/api/patterns?${params.toString()}`);
    return resp.data;
  },

  async getNewPatterns(lookbackDays = 1, minConfidence = 0.7, limit = 2000, window: PatternWindow = 22): Promise<NewPatternsResponse> {
    const params = new URLSearchParams({
      lookback_days: lookbackDays.toString(),
      min_confidence: minConfidence.toString(),
      limit: limit.toString(),
      window: window.toString(),
    });
    const resp = await requests.get(`/api/patterns/new?${params.toString()}`);
    return resp.data;
  },

  async getSummary(): Promise<PatternsSummaryResponse> {
    const resp = await requests.get('/api/patterns/summary');
    return resp.data;
  },

  async getFeatured(): Promise<PatternsFeaturedResponse> {
    const resp = await requests.get('/api/patterns/featured');
    return resp.data;
  },
};

// Get patterns with filters
export function getPatterns(filters?: {
  date?: string
  pattern_type?: string
  ticker?: string
  min_confidence?: number
  new_only?: boolean
  lookback_days?: number
  limit?: number
}) {
  return async (dispatch: Dispatch<PatternsAction>): Promise<void> => {
    const request = (): PatternsAction => ({ type: PatternsConstants.PATTERNS_REQUEST });
    const success = (payload: PatternsResponse): PatternsAction => ({
      type: PatternsConstants.PATTERNS_SUCCESS,
      payload,
    });
    const failure = (error: string): PatternsAction => ({
      type: PatternsConstants.PATTERNS_FAILURE,
      error,
    });

    dispatch(request());
    try {
      const content = await patternsService.getPatterns(filters);
      dispatch(success(content));
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch(failure((error as any)?.response?.data?.error || 'Failed to fetch patterns'));
    }
  };
}

// Get new patterns (detected today/this week)
export function getNewPatterns(lookbackDays = 1, minConfidence = 0.7, limit = 2000, window: PatternWindow = 22) {
  return async (dispatch: Dispatch<PatternsAction>): Promise<void> => {
    const request = (): PatternsAction => ({ type: PatternsConstants.NEW_PATTERNS_REQUEST });
    const success = (payload: NewPatternsResponse): PatternsAction => ({
      type: PatternsConstants.NEW_PATTERNS_SUCCESS,
      payload,
    });
    const failure = (error: string): PatternsAction => ({
      type: PatternsConstants.NEW_PATTERNS_FAILURE,
      error,
    });

    dispatch(request());
    try {
      const content = await patternsService.getNewPatterns(lookbackDays, minConfidence, limit, window);
      dispatch(success(content));
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch(failure((error as any)?.response?.data?.error || 'Failed to fetch new patterns'));
    }
  };
}

// Get patterns summary
export function getPatternsSummary() {
  return async (dispatch: Dispatch<PatternsAction>): Promise<void> => {
    const request = (): PatternsAction => ({ type: PatternsConstants.PATTERNS_SUMMARY_REQUEST });
    const success = (payload: PatternsSummaryResponse): PatternsAction => ({
      type: PatternsConstants.PATTERNS_SUMMARY_SUCCESS,
      payload,
    });
    const failure = (error: string): PatternsAction => ({
      type: PatternsConstants.PATTERNS_SUMMARY_FAILURE,
      error,
    });

    dispatch(request());
    try {
      const content = await patternsService.getSummary();
      dispatch(success(content));
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch(failure((error as any)?.response?.data?.error || 'Failed to fetch patterns summary'));
    }
  };
}

// Get featured patterns
export function getFeaturedPatterns() {
  return async (dispatch: Dispatch<PatternsAction>): Promise<void> => {
    const request = (): PatternsAction => ({ type: PatternsConstants.PATTERNS_FEATURED_REQUEST });
    const success = (payload: PatternsFeaturedResponse): PatternsAction => ({
      type: PatternsConstants.PATTERNS_FEATURED_SUCCESS,
      payload,
    });
    const failure = (error: string): PatternsAction => ({
      type: PatternsConstants.PATTERNS_FEATURED_FAILURE,
      error,
    });

    dispatch(request());
    try {
      const content = await patternsService.getFeatured();
      dispatch(success(content));
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      dispatch(failure((error as any)?.response?.data?.error || 'Failed to fetch featured patterns'));
    }
  };
}
